/**
 * Dependency graph and document-order scoping (docs/SHEETS.md 3).
 *
 * "Reactive, in document order, over an explicit dependency graph." Two consequences
 * follow from taking that literally, and both are load-bearing:
 *
 * 1. A symbol resolves to the nearest *preceding* definition. So a later definition
 *    shadows an earlier one -- which is how `f_y := 275` can legitimately override a
 *    project default further up -- and the shadow is recorded so the UI can say so
 *    rather than let it happen silently.
 *
 * 2. Every dependency edge points backwards, so **a cycle inside one sheet is
 *    structurally impossible**. `x := x + 1` is not a cycle; it reads the previous
 *    `x` and defines a new one, exactly as it would on paper. A self-reference with
 *    no earlier definition is an *undefined symbol*, which is the more useful thing
 *    to tell someone.
 *
 * `detectCycle` is kept and tested anyway, because cross-sheet references
 * (ADR-0010) introduce edges this rule does not constrain, and because a silent
 * infinite loop is precisely the failure mode SHEETS.md 3 forbids.
 */
import { parseMath } from './parse'
import type { Cell, CellId, EvalError } from './types'

export interface CellAnalysis {
  readonly cellId: CellId
  readonly index: number
  /** Canonical symbol keys this cell defines, in order. */
  readonly defines: readonly string[]
  /** Canonical symbol keys this cell reads. */
  readonly uses: readonly string[]
  /** A structural problem found without evaluating anything. */
  readonly error?: EvalError
}

export interface SheetGraph {
  /** Evaluation order. Identical to document order, by construction. */
  readonly order: readonly CellId[]
  readonly analysis: ReadonlyMap<CellId, CellAnalysis>
  /** Symbol -> defining cell, as visible *to* each cell (before its own definitions). */
  readonly scopeAt: ReadonlyMap<CellId, ReadonlyMap<string, CellId>>
  /** Symbol -> defining cell at the end of the document. Drives the variables panel. */
  readonly finalScope: ReadonlyMap<string, CellId>
  readonly dependsOn: ReadonlyMap<CellId, readonly CellId[]>
  readonly dependents: ReadonlyMap<CellId, readonly CellId[]>
  readonly errors: ReadonlyMap<CellId, EvalError>
  /** A cell that redefines a symbol already in scope -> the cell it shadows. */
  readonly shadows: ReadonlyMap<CellId, CellId>
}

/** What a single cell defines and reads, without reference to its neighbours. */
export function analyzeCell(cell: Cell, index: number): CellAnalysis {
  const base = { cellId: cell.id, index }

  switch (cell.type) {
    case 'math': {
      const parsed = parseMath(cell.latex)
      switch (parsed.kind) {
        case 'empty':
          return { ...base, defines: [], uses: [] }
        case 'assign':
          return { ...base, defines: [parsed.target], uses: parsed.uses }
        case 'evaluate':
          return { ...base, defines: [], uses: parsed.uses }
        case 'error':
          return {
            ...base,
            defines: [],
            uses: [],
            error: {
              kind: 'syntax',
              messageKey: parsed.messageKey,
              ...(parsed.params ? { params: parsed.params } : {}),
            },
          }
      }
      break
    }
    case 'table': {
      const defines: string[] = []
      for (const row of cell.rows) {
        if (row.symbol) defines.push(row.symbol)
      }
      return { ...base, defines, uses: [] }
    }
    case 'plot': {
      const parsed = parseMath(cell.latex)
      const uses = parsed.kind === 'assign' || parsed.kind === 'evaluate' ? [...parsed.uses] : []
      // The swept symbol is supplied by the plot itself, not read from scope.
      return { ...base, defines: [], uses: uses.filter((key) => key !== cell.sweep) }
    }
    case 'text':
    case 'image':
      return { ...base, defines: [], uses: [] }
  }

  return { ...base, defines: [], uses: [] }
}

export function buildGraph(cells: readonly Cell[]): SheetGraph {
  const order: CellId[] = []
  const analysis = new Map<CellId, CellAnalysis>()
  const scopeAt = new Map<CellId, ReadonlyMap<string, CellId>>()
  const dependsOn = new Map<CellId, CellId[]>()
  const dependents = new Map<CellId, CellId[]>()
  const errors = new Map<CellId, EvalError>()
  const shadows = new Map<CellId, CellId>()

  const scope = new Map<string, CellId>()

  cells.forEach((cell, index) => {
    const cellAnalysis = analyzeCell(cell, index)
    order.push(cell.id)
    analysis.set(cell.id, cellAnalysis)
    dependsOn.set(cell.id, [])
    if (!dependents.has(cell.id)) dependents.set(cell.id, [])

    // What this cell can see: everything defined strictly above it.
    scopeAt.set(cell.id, new Map(scope))

    if (cellAnalysis.error) {
      errors.set(cell.id, cellAnalysis.error)
    }

    const missing: string[] = []
    for (const key of cellAnalysis.uses) {
      const source = scope.get(key)
      if (source === undefined) {
        missing.push(key)
        continue
      }
      const edges = dependsOn.get(cell.id)
      if (edges && !edges.includes(source)) edges.push(source)
      const back = dependents.get(source)
      if (back && !back.includes(cell.id)) back.push(cell.id)
    }

    if (missing.length > 0 && !errors.has(cell.id)) {
      errors.set(cell.id, {
        kind: 'undefined',
        messageKey: 'sheets.error.undefined_symbol',
        params: { symbol: missing.join(', '), count: missing.length },
      })
    }

    for (const key of cellAnalysis.defines) {
      const prior = scope.get(key)
      if (prior !== undefined && prior !== cell.id) {
        shadows.set(cell.id, prior)
      }
      scope.set(key, cell.id)
    }
  })

  return {
    order,
    analysis,
    scopeAt,
    finalScope: scope,
    dependsOn,
    dependents,
    errors,
    shadows,
  }
}

/**
 * Every cell that must be recomputed when `changed` changes: the cells themselves
 * plus everything transitively downstream. This is the whole of "any value related
 * to another field updates automatically" -- the reactive part is just this set,
 * re-evaluated in document order.
 */
export function downstreamOf(graph: SheetGraph, changed: Iterable<CellId>): Set<CellId> {
  const out = new Set<CellId>()
  const queue: CellId[] = []

  for (const id of changed) {
    if (!out.has(id)) {
      out.add(id)
      queue.push(id)
    }
  }

  while (queue.length > 0) {
    const id = queue.shift()
    if (id === undefined) continue
    for (const next of graph.dependents.get(id) ?? []) {
      if (!out.has(next)) {
        out.add(next)
        queue.push(next)
      }
    }
  }

  return out
}

/**
 * Every cell `seeds` transitively reads. The mirror of `downstreamOf`, and what a
 * drag has to carry with it: bringing one dependency along is not enough when that
 * dependency has dependencies of its own.
 */
export function upstreamOf(graph: SheetGraph, seeds: Iterable<CellId>): Set<CellId> {
  const out = new Set<CellId>()
  const queue: CellId[] = []

  for (const id of seeds) {
    if (!out.has(id)) {
      out.add(id)
      queue.push(id)
    }
  }

  while (queue.length > 0) {
    const id = queue.shift()
    if (id === undefined) continue
    for (const prev of graph.dependsOn.get(id) ?? []) {
      if (!out.has(prev)) {
        out.add(prev)
        queue.push(prev)
      }
    }
  }

  return out
}

/** Cells to re-evaluate, in document order. */
export function evaluationOrder(graph: SheetGraph, dirty: ReadonlySet<CellId>): CellId[] {
  return graph.order.filter((id) => dirty.has(id))
}

/**
 * General cycle detection over an adjacency map. Unreachable for a single sheet
 * (see the file header) but not for cross-sheet references, and cheap enough that
 * having it is better than trusting an invariant to hold forever.
 *
 * Returns one cycle as a path, or null.
 */
export function detectCycle(
  nodes: readonly CellId[],
  edges: ReadonlyMap<CellId, readonly CellId[]>,
): CellId[] | null {
  const WHITE = 0
  const GREY = 1
  const BLACK = 2
  const colour = new Map<CellId, number>()
  const stack: CellId[] = []

  for (const node of nodes) colour.set(node, WHITE)

  const visit = (node: CellId): CellId[] | null => {
    colour.set(node, GREY)
    stack.push(node)

    for (const next of edges.get(node) ?? []) {
      const state = colour.get(next) ?? WHITE
      if (state === GREY) {
        const from = stack.indexOf(next)
        return [...stack.slice(from), next]
      }
      if (state === WHITE) {
        const found = visit(next)
        if (found) return found
      }
    }

    stack.pop()
    colour.set(node, BLACK)
    return null
  }

  for (const node of nodes) {
    if ((colour.get(node) ?? WHITE) === WHITE) {
      const found = visit(node)
      if (found) return found
    }
  }

  return null
}
